// UC20-06 / UC20-07 — manual import into the medicine nomenclator.
// per docs/shape-uc-20-medicine-nomenclator.md §4, §5
// per docs/shape-ops-medicine-identity.md §D3, §D4
// Three steps, as the wireframe draws them: choose the file, validate it, then
// import it. The validation pass is a real dry run of the same checks the import
// makes, the catalogue's included, so the report cannot disagree with the write.
// The import is one action (FIAB-08): it is offered only for a file without errors,
// and a file the catalogue refuses imports nothing. The wireframe's "only the valid
// rows are imported" is the one place this screen departs from it.
import { Fragment, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudFileInput,
  MudFileItem,
  MudIcon,
  MudInfoBox,
  MudToast,
  MudStepper,
  MudSpinner,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import { apiClient, apiErrorKey } from '../../app/api-client';
import { Breadcrumb, PageHeader } from '@simsm/ui-shared';
import './medicines-import.css';

// mud-table's own column/row types are not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  width?: string;
}
type TableRowData = Record<string, string>;

// Codes and their values, never prose: the sentence is written here, in the
// reader's language (docs/shape-ops-localized-output.md §2.4).
interface ImportRowError {
  rowIndex: number;
  column: string;
  code: string;
  value?: string;
  // What a catalogue refusal collided with: the medicine holding the code, the code
  // as stored, and its type.
  medicine?: string;
  existing?: string;
  kind?: string;
}

interface ImportSummary {
  received: number;
  created: number;
  updated: number;
  skippedAuthorized: number;
  errors: number;
  applied?: boolean;
}

interface ImportResult {
  dryRun: boolean;
  imported: number;
  totalRows: number;
  validRows: number;
  errorRows: number;
  authorizedRows: number;
  unauthorizedRows: number;
  errors: ImportRowError[];
  summary: ImportSummary | null;
}

const TEMPLATE_URL = '/api/v1/admin/medicines/template.xlsx';
const KNOWN_ERROR_CODES = [
  'MISSING_COLUMN',
  'REQUIRED',
  'UNKNOWN_TIP',
  'INVALID_DATE',
  'DUPLICATE_IN_FILE',
  'NO_WORKSHEET',
  'NO_DATA_ROWS',
  'CODE_TAKEN',
  'CODE_CASE_VARIANT',
  'CODE_KIND_CONFLICT',
  'ROW_REFUSED',
];
const CODE_KINDS = ['AUTHORIZED', 'UNAUTHORIZED', 'BARCODE'];

function stepStatus(index: number, current: number): 'pending' | 'current' | 'completed' {
  if (index < current) return 'completed';
  if (index === current) return 'current';
  return 'pending';
}

// The formats the field takes; MUD writes the caption naming them in the page language.
const MEDICINES_IMPORT_ACCEPT = '.xlsx,.csv';

export function MedicinesImportPage(): JSX.Element {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<ImportResult | null>(null);
  const [outcome, setOutcome] = useState<ImportResult | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  async function send(selected: File, dryRun: boolean): Promise<ImportResult> {
    const form = new FormData();
    form.append('file', selected);
    const { data } = await apiClient.post<ImportResult>(
      `/admin/medicines/import${dryRun ? '?dryRun=true' : ''}`,
      form,
    );
    return data;
  }

  const validate = useMutation({
    mutationFn: (selected: File) => send(selected, true),
    onSuccess: (data) => {
      setValidation(data);
      setErrorMessage(null);
    },
    // Validation and import fail differently and say so: the dry run rejects a
    // file, the import stops a write that may already have started.
    onError: (err) => {
      setErrorMessage(t(apiErrorKey(err, 'uc20_import.error.validate_failed')));
      setValidation(null);
    },
  });

  const runImport = useMutation({
    mutationFn: (selected: File) => send(selected, false),
    onSuccess: (data) => {
      setOutcome(data);
      setErrorMessage(null);
      queryClient.invalidateQueries({ queryKey: ['admin-medicines'] });
    },
    onError: (err) => setErrorMessage(t(apiErrorKey(err, 'uc20_import.error.import_failed'))),
  });

  // The file is chosen, then validated, then imported; the tracker names where
  // the administrator is (ACCS-08).
  const step = outcome ? 2 : validation ? 1 : 0;
  const busy = validate.isPending || runImport.isPending;

  function reset(): void {
    setFile(null);
    setValidation(null);
    setOutcome(null);
    setErrorMessage(null);
  }

  function errorLabel(rowError: ImportRowError): string {
    if (!KNOWN_ERROR_CODES.includes(rowError.code)) {
      return t('uc20_import.error.unknown', { code: rowError.code });
    }
    const key = `uc20_import.error.${rowError.code}`;
    const kind = rowError.kind ?? '';
    return t(key, {
      column: rowError.column,
      value: rowError.value ?? '',
      medicine: rowError.medicine ?? '',
      existing: rowError.existing ?? '',
      kind: CODE_KINDS.includes(kind) ? t(`medicines.kind.${kind}`) : kind,
    });
  }

  const report = outcome ?? validation;
  // Imported means the catalogue committed the file; a refused one wrote nothing.
  const imported = outcome !== null && outcome.imported > 0;

  const errorColumns: TableColumn[] = [
    { key: 'row', label: t('uc20_import.col.row'), width: '80px' },
    { key: 'column', label: t('uc20_import.col.column'), width: '200px' },
    { key: 'message', label: t('uc20_import.col.message') },
  ];
  const errorRows: TableRowData[] = (report?.errors ?? []).map((rowError, i) => ({
    id: `${rowError.rowIndex}-${i}`,
    row: String(rowError.rowIndex),
    column: rowError.column,
    message: errorLabel(rowError),
  }));

  return (
    <section className="simsm-page simsm-import simsm-import--nomenclator">
      {/* The wireframe (wireframes/react/src/pages/ImportNomenclator.tsx:52) puts the
          way back in a breadcrumb, not in a header button, and the trail names the
          register this import writes into. The "Inapoi la medicamente" button it
          replaces said the same thing twice. */}
      <Breadcrumb
        label={t('common.breadcrumb.aria_label')}
        crumbs={[
          { label: t('uc20_import.breadcrumb.register'), to: '/system-admin/medicines' },
          { label: t('uc20_import.breadcrumb.current') },
        ]}
      />

      <PageHeader
        title={t('uc20_import.title')}
        lede={t('uc20_import.lede')}
        actions={
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => window.open(TEMPLATE_URL, '_blank')}
          >
            <MudIcon slot="icon-start" name="download" size={16} />
            {t('uc20_import.download_template')}
          </MudButton>
        }
      />

      <MudStepper
        className="simsm-import__steps"
        currentStep={step}
        aria-label={t('uc20_import.steps.aria')}
        steps={[
          { label: t('uc20_import.step.choose'), status: stepStatus(0, step) },
          { label: t('uc20_import.step.validate'), status: stepStatus(1, step) },
          { label: t('uc20_import.step.result'), status: stepStatus(2, step) },
        ]}
      />

      {errorMessage ? (
        <MudInfoBox emphasis="strong" role="alert" variant="error">
          {errorMessage}
        </MudInfoBox>
      ) : null}

      {step === 0 ? (
        <div className="simsm-import__panel">
          <MudFileInput
            accept={MEDICINES_IMPORT_ACCEPT}
            variant="dropzone"
            size="lg"
            onMudChange={(e: CustomEvent<{ files: File[] }>) => {
              const next = e.detail.files[0] ?? null;
              setFile(next);
              setValidation(null);
              setOutcome(null);
            }}
          />
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={!file || busy}
            onClick={() => file && validate.mutate(file)}
          >
            {validate.isPending ? (
              <MudSpinner slot="icon-start" size="sm" label={t('common.loading')} />
            ) : (
              <MudIcon slot="icon-start" name="upload" size={16} />
            )}
            {validate.isPending ? t('uc20_import.validating') : t('uc20_import.action.validate')}
          </MudButton>
        </div>
      ) : null}

      {report && file ? (
        <>
          <MudFileItem
            removeLabel={t('common.file_input.remove')}
            filename={file.name}
            size={file.size}
            state="success"
            noRemove={runImport.isPending}
            onMudRemove={reset}
          />

          <div className="simsm-import__tiles">
            <div className="simsm-import__tile">
              <span className="simsm-import__tile-key">{t('uc20_import.tile.valid')}</span>
              <span className="simsm-import__tile-value">{report.validRows}</span>
            </div>
            <div className="simsm-import__tile">
              <span className="simsm-import__tile-key">{t('uc20_import.tile.errors')}</span>
              <span className="simsm-import__tile-value">{report.errorRows}</span>
            </div>
            <div className="simsm-import__tile">
              <span className="simsm-import__tile-key">{t('uc20_import.tile.kinds')}</span>
              <span className="simsm-import__tile-value">
                {report.authorizedRows} / {report.unauthorizedRows}
              </span>
            </div>
          </div>

          {imported && outcome?.summary ? (
            <div className="simsm-inline-outcome">
              <MudToast closable={false} variant="success">
                {t('uc20_import.success', {
                  received: outcome.summary.received,
                  imported: outcome.summary.created + outcome.summary.updated,
                  created: outcome.summary.created,
                  updated: outcome.summary.updated,
                })}
              </MudToast>
            </div>
          ) : outcome ? (
            <MudInfoBox emphasis="strong" role="alert" variant="error">
              {t('uc20_import.errors_heading', { count: outcome.errorRows })}
            </MudInfoBox>
          ) : report.errorRows > 0 ? (
            <MudInfoBox
              emphasis="strong"
              variant="warning"
              titleText={t('uc20_import.partial.title')}
            >
              {t('uc20_import.partial.body', {
                errors: report.errorRows,
                valid: report.validRows,
              })}
            </MudInfoBox>
          ) : (
            <MudInfoBox emphasis="strong" variant="info" titleText={t('uc20_import.clean.title')}>
              {t('uc20_import.clean.body', { valid: report.validRows })}
            </MudInfoBox>
          )}

          {report.errors.length > 0 ? (
            <div className="simsm-import__errors">
              <div className="simsm-import__errors-table">
                <MudTable
                  className="simsm-table"
                  columns={errorColumns}
                  rows={errorRows}
                  rowStyle="divided"
                  disableSort={true}
                  aria-label={t('uc20_import.partial.title')}
                >
                  {report.errors.map((rowError, i) => (
                    <Fragment key={`${rowError.rowIndex}-${i}`}>
                      <span slot={`cell-column-${i}`}>
                        <MudTag variant="status" semantic="neutral" label={rowError.column} />
                      </span>
                      {/* Slotted so the sentence wraps (wireframe `tbl compact`
                          cells wrap) instead of taking mud-table's one-line ellipsis. */}
                      <span slot={`cell-message-${i}`} className="simsm-import__errors-message">
                        {errorLabel(rowError)}
                      </span>
                    </Fragment>
                  ))}
                </MudTable>
              </div>
              {report.errorRows > report.errors.length ? (
                <p className="simsm-import__hint">
                  {t('uc20_import.errors_truncated', {
                    shown: report.errors.length,
                    total: report.errorRows,
                  })}
                </p>
              ) : null}
            </div>
          ) : null}

          <div className="simsm-import__actions">
            <MudButton shape="circular" variant="neutral" size="md" disabled={busy} onClick={reset}>
              {t('uc20_import.action.other_file')}
            </MudButton>
            {outcome ? (
              <MudButton
                shape="circular"
                variant="primary"
                size="md"
                onClick={() => navigate('/system-admin/medicines')}
              >
                {t('uc20_import.action.back_to_registry')}
              </MudButton>
            ) : (
              <MudButton
                shape="circular"
                variant="primary"
                size="md"
                disabled={busy || report.validRows === 0 || report.errorRows > 0}
                onClick={() => file && runImport.mutate(file)}
              >
                {runImport.isPending ? (
                  <MudSpinner slot="icon-start" size="sm" label={t('common.loading')} />
                ) : null}
                {t('uc20_import.action.import_valid', { valid: report.validRows })}
              </MudButton>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
